import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../../components/SEO';

const AIHub = () => {
  const aiCapabilities = [
    {
      title: 'Intelligent Automation',
      description: 'Streamline operations with AI-powered process automation that learns and adapts to your business workflows.',
      icon: 'M13 10V3L4 14h7v7l9-11h-7z',
      link: '/ai-hub/intelligent-automation',
      features: ['Process Mining', 'RPA Integration', 'Workflow Optimization']
    },
    {
      title: 'Predictive Analytics',
      description: 'Leverage machine learning models to forecast trends, predict outcomes, and make data-driven decisions.',
      icon: 'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z',
      link: '/ai-hub/predictive-analytics',
      features: ['Demand Forecasting', 'Risk Assessment', 'Trend Analysis']
    },
    {
      title: 'Natural Language Processing',
      description: 'Transform unstructured text into actionable insights with advanced NLP and conversational AI capabilities.',
      icon: 'M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z',
      link: '/ai-hub/nlp',
      features: ['Sentiment Analysis', 'Entity Extraction', 'Text Classification']
    },
    {
      title: 'Computer Vision',
      description: 'Enable machines to interpret and analyze visual information from the world with deep learning models.',
      icon: 'M15 12a3 3 0 11-6 0 3 3 0 016 0z M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z',
      link: '/ai-hub/computer-vision',
      features: ['Object Detection', 'Image Recognition', 'Video Analytics']
    },
    {
      title: 'Intelligent Chatbots',
      description: 'Deploy conversational AI agents that provide 24/7 customer support with human-like interactions.',
      icon: 'M17 8h2a2 2 0 012 2v6a2 2 0 01-2 2h-2v4l-4-4H9a1.994 1.994 0 01-1.414-.586m0 0L11 14h4a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2v4l.586-.586z',
      link: '/ai-hub/intelligent-chatbots',
      features: ['Multi-Channel Support', 'Context Awareness', 'Intent Recognition']
    },
    {
      title: 'MLOps Framework',
      description: 'Operationalize machine learning models with automated pipelines for continuous training and deployment.',
      icon: 'M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4m0 5c0 2.21-3.582 4-8 4s-8-1.79-8-4',
      link: '/ai-hub/mlops-framework',
      features: ['Model Versioning', 'Automated Pipelines', 'Performance Monitoring']
    },
    {
      title: 'AI Ethics & Governance',
      description: 'Implement responsible AI practices with frameworks for fairness, transparency, and accountability.',
      icon: 'M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z',
      link: '/ai-hub/ai-ethics-governance',
      features: ['Bias Detection', 'Explainability', 'Compliance Framework']
    },
    {
      title: 'Generative AI',
      description: 'Harness the power of generative models to create content, designs, and solutions at scale.',
      icon: 'M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z',
      link: '/ai-hub/generative-ai',
      features: ['Content Generation', 'Code Synthesis', 'Creative Design']
    },
    {
      title: 'AI Readiness Assessment',
      description: 'Evaluate your organization\'s AI maturity and develop a strategic roadmap for successful implementation.',
      icon: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4',
      link: '/ai-hub/ai-readiness',
      features: ['Maturity Assessment', 'Gap Analysis', 'Implementation Roadmap']
    }
  ];

  const coreCapabilities = [
    {
      title: 'Intelligent Routing Engine',
      description: 'Dynamic routing and orchestration of AI workloads across distributed systems',
      icon: 'M8.111 16.404a5.5 5.5 0 017.778 0M12 20h.01m-7.08-7.071c3.904-3.905 10.236-3.905 14.141 0M1.394 9.393c5.857-5.857 15.355-5.857 21.213 0'
    },
    {
      title: 'Temporal Analysis Engine',
      description: 'Time-series analysis and pattern recognition for predictive insights',
      icon: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z'
    },
    {
      title: 'Rules & Decision Engines',
      description: 'Complex business logic automation with AI-enhanced decision-making',
      icon: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z'
    },
    {
      title: 'Intelligence Amplification',
      description: 'Augment human decision-making with AI-powered insights and recommendations',
      icon: 'M13 10V3L4 14h7v7l9-11h-7z'
    }
  ];

  const integrationPartners = [
    'AWS', 'Azure', 'Google Cloud', 'TensorFlow', 'PyTorch', 'Hugging Face',
    'OpenAI', 'Anthropic', 'Databricks', 'Snowflake'
  ];

  return (
    <>
      <SEO 
        title="AI Hub - Enterprise AI Solutions & Machine Learning"
        description="Explore Abacies Logiciels' comprehensive AI Hub featuring intelligent automation, predictive analytics, NLP, computer vision, and more. Transform your business with cutting-edge artificial intelligence."
        keywords="AI hub, artificial intelligence, machine learning, predictive analytics, NLP, computer vision, intelligent automation, MLOps, generative AI"
      />

      {/* Hero Section */}
      <section className="hero-section py-10">
        <div className="page-container">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-block mb-4 px-4 py-2 bg-blush-light/30 rounded-full">
              <span className="text-primary-red font-semibold">AI Innovation Center</span>
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 text-charcoal animate-fade-in">
              Welcome to the <span className="text-gradient">AI Hub</span>
            </h1>
            <p className="text-xl text-titanium leading-relaxed animate-slide-up mb-8">
              Discover our comprehensive suite of artificial intelligence solutions designed to transform your business. 
              From intelligent automation to advanced machine learning, explore how AI can drive innovation and growth.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center animate-slide-up" style={{ animationDelay: '0.1s' }}>
              <Link to="/contact" className="btn-primary">
                Schedule AI Consultation
              </Link>
              <Link to="/ai-hub/ai-readiness" className="btn-secondary">
                Assess AI Readiness
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Key Statistics */}
      <section className="py-2 bg-white">
        <div className="page-container">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-5xl mx-auto">
            <div className="text-center">
              <div className="text-4xl font-bold text-primary-red mb-2">500+</div>
              <div className="text-sm text-titanium">AI Models Deployed</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-primary-red mb-2">95%</div>
              <div className="text-sm text-titanium">Accuracy Rate</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-primary-red mb-2">40%</div>
              <div className="text-sm text-titanium">Cost Reduction</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-primary-red mb-2">24/7</div>
              <div className="text-sm text-titanium">AI Operations</div>
            </div>
          </div>
        </div>
      </section>

      {/* Core AI Capabilities */}
      <section className="py-3 md:py-3 bg-alabaster">
        <div className="page-container">
          <div className="text-center mb-12">
            <h2 className="section-title">Core AI Orchestration</h2>
            <p className="section-subtitle max-w-3xl mx-auto">
              Enterprise-grade AI infrastructure powering intelligent business operations
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-4">
            {coreCapabilities.map((capability, index) => (
              <div key={index} className="card text-center hover:transform hover:scale-105 transition-all duration-300">
                <div className="w-16 h-16 bg-blush-light/40 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-primary-red" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={capability.icon} />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-charcoal mb-2">{capability.title}</h3>
                <p className="text-sm text-titanium">{capability.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* AI Solutions Grid */}
      <section className="py-5 md:py-5 bg-white">
        <div className="page-container">
          <div className="text-center mb-12">
            <h2 className="section-title">AI Solutions Portfolio</h2>
            <p className="section-subtitle max-w-3xl mx-auto">
              Comprehensive AI capabilities tailored to your specific business needs
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {aiCapabilities.map((capability, index) => (
              <Link
                key={index}
                to={capability.link}
                className="group card hover:shadow-xl transition-all duration-300 hover:transform hover:scale-105"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="w-14 h-14 bg-blush-light/40 rounded-xl flex items-center justify-center group-hover:bg-primary-red group-hover:scale-110 transition-all duration-300">
                    <svg className="w-7 h-7 text-primary-red group-hover:text-white transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={capability.icon} />
                    </svg>
                  </div>
                  <svg className="w-5 h-5 text-cool-grey group-hover:text-primary-red group-hover:translate-x-1 transition-all duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold mb-3 text-charcoal group-hover:text-primary-red transition-colors">
                  {capability.title}
                </h3>
                <p className="text-titanium mb-4">{capability.description}</p>
                <div className="flex flex-wrap gap-2">
                  {capability.features.map((feature, idx) => (
                    <span key={idx} className="px-3 py-1 bg-blush-light/30 text-primary-red text-xs rounded-full">
                      {feature}
                    </span>
                  ))}
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Technology Stack */}
      <section className="py-5 md:py-5 bg-alabaster">
        <div className="page-container">
          <div className="text-center mb-12">
            <h2 className="section-title">Technology & Integration</h2>
            <p className="section-subtitle max-w-3xl mx-auto">
              Built on industry-leading platforms and frameworks
            </p>
          </div>
          <div className="max-w-5xl mx-auto">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-6 mb-12">
              {integrationPartners.map((partner, index) => (
                <div key={index} className="card text-center py-6">
                  <div className="text-lg font-semibold text-charcoal">{partner}</div>
                </div>
              ))}
            </div>
            <div className="card bg-white text-center">
              <h3 className="text-xl font-semibold text-charcoal mb-4">
                Seamless Integration Capabilities
              </h3>
              <p className="text-titanium mb-6">
                Our AI solutions integrate seamlessly with your existing technology stack, 
                ensuring smooth deployment and minimal disruption to your operations.
              </p>
              <Link to="/services/system-integration" className="text-primary-red font-semibold hover:underline">
                Explore Integration Services →
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-5 md:py-5 bg-white">
        <div className="page-container">
          <div className="max-w-6xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-3xl md:text-4xl font-bold mb-6 text-charcoal">
                  Why Choose Our <span className="text-gradient">AI Solutions</span>
                </h2>
                <div className="space-y-6">
                  {[
                    {
                      title: 'Enterprise-Grade Scalability',
                      description: 'Built to handle millions of transactions with consistent performance and reliability'
                    },
                    {
                      title: 'Industry-Leading Accuracy',
                      description: 'Advanced models achieving 95%+ accuracy across diverse use cases and industries'
                    },
                    {
                      title: 'Rapid Deployment',
                      description: 'Pre-built frameworks and templates enable quick implementation and time-to-value'
                    },
                    {
                      title: 'Continuous Learning',
                      description: 'Self-improving models that adapt and optimize based on real-world performance data'
                    },
                    {
                      title: 'Explainable AI',
                      description: 'Transparent decision-making processes with full auditability and interpretability'
                    },
                    {
                      title: 'Expert Support',
                      description: 'Dedicated AI specialists providing ongoing optimization and technical guidance'
                    }
                  ].map((benefit, index) => (
                    <div key={index} className="flex items-start">
                      <div className="w-6 h-6 bg-primary-red rounded-full flex items-center justify-center flex-shrink-0 mt-1 mr-4">
                        <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-charcoal mb-1">{benefit.title}</h3>
                        <p className="text-titanium">{benefit.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="bg-alabaster rounded-2xl p-8">
                <h3 className="text-2xl font-bold text-charcoal mb-6">AI Capability Matrix</h3>
                <div className="space-y-4">
                  {[
                    { capability: 'Computer Vision', level: 95 },
                    { capability: 'Natural Language Processing', level: 92 },
                    { capability: 'Predictive Analytics', level: 90 },
                    { capability: 'Intelligent Automation', level: 88 },
                    { capability: 'Conversational AI', level: 85 }
                  ].map((item, index) => (
                    <div key={index}>
                      <div className="flex justify-between mb-2">
                        <span className="text-sm font-medium text-charcoal">{item.capability}</span>
                        <span className="text-sm font-bold text-primary-red">{item.level}%</span>
                      </div>
                      <div className="w-full bg-platinum rounded-full h-2">
                        <div 
                          className="bg-primary-red h-2 rounded-full transition-all duration-1000"
                          style={{ width: `${item.level}%` }}
                        ></div>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-8 p-6 bg-white rounded-lg border border-platinum">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="text-lg font-semibold text-charcoal">Overall AI Maturity</h4>
                    <span className="text-3xl font-bold text-primary-red">4.6/5</span>
                  </div>
                  <p className="text-sm text-titanium">
                    Industry-leading AI capabilities validated by independent assessments and client success metrics
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="py-8 md:py-8 bg-alabaster">
        <div className="page-container">
          <div className="text-center mb-12">
            <h2 className="section-title">Real-World Applications</h2>
            <p className="section-subtitle max-w-3xl mx-auto">
              See how our AI solutions drive measurable results across industries
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {[
              {
                industry: 'Healthcare',
                useCase: 'Medical Image Analysis',
                result: '40% faster diagnosis with 98% accuracy',
                icon: 'M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z'
              },
              {
                industry: 'Finance',
                useCase: 'Fraud Detection',
                result: '99.2% fraud detection rate, $2M saved annually',
                icon: 'M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z'
              },
              {
                industry: 'Retail',
                useCase: 'Demand Forecasting',
                result: '25% reduction in inventory costs',
                icon: 'M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z'
              },
              {
                industry: 'Manufacturing',
                useCase: 'Predictive Maintenance',
                result: '60% reduction in equipment downtime',
                icon: 'M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z M15 12a3 3 0 11-6 0 3 3 0 016 0z'
              },
              {
                industry: 'Customer Service',
                useCase: 'AI Chatbots',
                result: '85% resolution rate, 50% cost savings',
                icon: 'M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z'
              },
              {
                industry: 'Logistics',
                useCase: 'Route Optimization',
                result: '30% fuel cost reduction, faster deliveries',
                icon: 'M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7'
              }
            ].map((useCase, index) => (
              <div key={index} className="card hover:shadow-xl transition-all duration-300">
                <div className="w-12 h-12 bg-blush-light/40 rounded-lg flex items-center justify-center mb-4">
                  <svg className="w-6 h-6 text-primary-red" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={useCase.icon} />
                  </svg>
                </div>
                <div className="text-sm text-primary-red font-semibold mb-2">{useCase.industry}</div>
                <h3 className="text-lg font-semibold text-charcoal mb-2">{useCase.useCase}</h3>
                <p className="text-titanium text-sm">{useCase.result}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-8 md:py-8 bg-primary-red text-white">
        <div className="page-container text-center">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6">
            Ready to Transform with AI?
          </h2>
          <p className="text-xl mb-8 opacity-90 max-w-3xl mx-auto">
            Let our AI experts help you identify opportunities, develop a strategy, and implement solutions 
            that deliver measurable business value.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact" className="bg-white text-primary-red px-8 py-4 rounded-lg text-lg font-semibold hover:bg-pearl-white transition-all duration-300 shadow-lg">
              Schedule Consultation
            </Link>
            <Link to="/ai-hub/ai-readiness" className="border-2 border-white text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-white hover:text-primary-red transition-all duration-300">
              Assess Your AI Readiness
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default AIHub;